import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Award, Sparkles, MessageCircle, Music2, Copy, Check, ArrowRight, ArrowLeft, Share2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FanProfilePage() {
  const { code } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/fan/${code}`);
        setProfile(data);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const joinLink = `${window.location.origin}/?ref=${code}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Loading fan profile...</p>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="fan-profile-not-found">
        <div className="glass-card p-10 max-w-md text-center border border-white/10">
          <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Fan profile not found</h1>
          <p className="text-gray-400 mb-6">The share link may be broken or the fan hasn't joined yet.</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const progress = Math.min((profile.referral_count / profile.threshold) * 100, 100);
  const isTop3 = profile.rank && profile.rank <= 3;
  const rankColors = {
    1: 'from-yellow-400 to-orange-500',
    2: 'from-slate-300 to-slate-500',
    3: 'from-amber-600 to-amber-800',
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="fan-profile-page">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/40 to-black" />
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(139,92,246,0.35), transparent 50%), radial-gradient(circle at 70% 80%, rgba(6,182,212,0.3), transparent 50%)',
      }} />

      <div className="container relative z-10 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition mb-8">
          <ArrowLeft className="w-4 h-4" /> @DaRealQJones
        </Link>

        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="glass-card p-8 md:p-10 border border-purple-500/20 mb-6">
            <div className="flex items-start gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${isTop3 ? rankColors[profile.rank] : 'from-purple-500 to-cyan-500'} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                <span className="text-white font-bold text-2xl">
                  {(profile.display_name || 'F')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">First-Listen Fan</span>
                <h1 className="text-3xl md:text-4xl font-bold leading-tight">{profile.display_name}</h1>
                {profile.rank && (
                  <p className="text-sm text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    <span>
                      Ranked <strong className="text-white">#{profile.rank}</strong> on the referral leaderboard
                    </span>
                    {profile.bonus_unlocked && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-pink-500/20 text-pink-300 border border-pink-500/30 uppercase tracking-wider font-semibold">
                        <Award className="w-3 h-3" /> Bonus Unlocked
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="rounded-xl border border-white/5 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Referrals</div>
                <div className="text-3xl font-bold">
                  {profile.referral_count}
                  <span className="text-sm text-gray-500 font-normal">/{profile.threshold}</span>
                </div>
                <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${profile.bonus_unlocked ? 'bg-gradient-to-r from-pink-500 to-orange-400' : 'bg-gradient-to-r from-purple-500 to-cyan-400'}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <div className="rounded-xl border border-white/5 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Reactions</div>
                <div className="text-3xl font-bold">{profile.reactions.length}</div>
                <div className="text-xs text-gray-500 mt-2">On voting snippets</div>
              </div>
            </div>

            {/* Share block */}
            <div className="rounded-xl bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-400/20 p-4">
              <p className="text-sm font-semibold text-white mb-2">
                Share your link to climb the leaderboard
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  readOnly
                  value={joinLink}
                  onClick={(e) => e.target.select()}
                  className="input-dark flex-1 font-mono text-xs sm:text-sm"
                  data-testid="fan-profile-share-link"
                />
                <button
                  onClick={handleCopy}
                  className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="fan-profile-copy-button"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy Link'}
                </button>
              </div>

              <ShareButtons
                joinLink={joinLink}
                displayName={profile.display_name}
                onCopy={handleCopy}
              />
            </div>
          </div>

          {/* Reactions */}
          {profile.reactions.length > 0 && (
            <div className="glass-card p-6 border border-white/5" data-testid="fan-profile-reactions">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-pink-400" />
                My Reactions
              </h2>
              <div className="space-y-3">
                {profile.reactions.map((r) => (
                  <article
                    key={r.id}
                    className="rounded-lg bg-white/5 p-4"
                    data-testid={`fan-profile-reaction-${r.id}`}
                  >
                    <p className="text-gray-200 leading-relaxed mb-2 whitespace-pre-wrap break-words">
                      {r.comment}
                    </p>
                    {r.snippet_title && (
                      <p className="text-[11px] text-cyan-300/80 flex items-center gap-1.5">
                        <Music2 className="w-3 h-3 flex-shrink-0" />
                        on <strong className="text-cyan-200">{r.snippet_title}</strong>
                        <span className="text-gray-500 ml-2">
                          &middot; {new Date(r.created_at).toLocaleDateString()}
                        </span>
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* Join CTA */}
          <div className="mt-6 glass-card p-6 border border-cyan-500/20 flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-white">Want in too?</h3>
              <p className="text-sm text-gray-400">
                Join the First-Listen list with {profile.display_name}'s link and hear every drop before anyone else.
              </p>
            </div>
            <Link
              to={`/?ref=${profile.referral_code}`}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
              data-testid="fan-profile-join-button"
            >
              Join with {profile.display_name} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Native platform share buttons - one tap to Twitter/WhatsApp/etc.
const ShareButtons = ({ joinLink, displayName, onCopy }) => {
  const encodedUrl = encodeURIComponent(joinLink);
  const shareText = `Just joined @DaRealQJones's First-Listen list — new music before anyone else. Come join me:`;
  const encodedText = encodeURIComponent(shareText);
  const encodedTextWithUrl = encodeURIComponent(`${shareText} ${joinLink}`);

  const targets = [
    {
      id: 'twitter',
      label: 'Twitter',
      color: 'from-slate-800 to-slate-950 hover:from-slate-700',
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      color: 'from-green-500 to-green-700 hover:from-green-400',
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.83 9.83 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413" />
        </svg>
      ),
      url: `https://wa.me/?text=${encodedTextWithUrl}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      color: 'from-blue-600 to-blue-800 hover:from-blue-500',
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
          <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.62 8.62 0 0 0-.653-.036 26.8 26.8 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.69 1.69 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12S0 5.417 0 12.044c0 5.628 3.874 10.35 9.101 11.647" />
        </svg>
      ),
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      id: 'telegram',
      label: 'Telegram',
      color: 'from-sky-500 to-sky-700 hover:from-sky-400',
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm4.962 7.224c.1-.002.321.023.465.14a.5.5 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    },
  ];

  const handleInstagram = async () => {
    if (onCopy) await onCopy();
    // Instagram has no web share intent - copy link then guide to paste
    // Toast is already fired from onCopy
  };

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: '@DaRealQJones First-Listen', text: shareText, url: joinLink });
      }
    } catch { /* user cancelled */ }
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid="fan-profile-share-buttons">
      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5">
        <Share2 className="w-3 h-3" /> Share to
      </p>
      <div className="flex flex-wrap gap-2">
        {targets.map((t) => (
          <a
            key={t.id}
            href={t.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-br ${t.color} transition-all hover:scale-105`}
            data-testid={`share-${t.id}`}
          >
            {t.icon} {t.label}
          </a>
        ))}
        <button
          onClick={handleInstagram}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-500 hover:from-fuchsia-400 transition-all hover:scale-105"
          data-testid="share-instagram"
          type="button"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
          </svg>
          Instagram
        </button>
        {canNativeShare && (
          <button
            onClick={handleNativeShare}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-white bg-white/10 hover:bg-white/20 transition-all hover:scale-105"
            data-testid="share-native"
            type="button"
          >
            <Share2 className="w-4 h-4" /> More
          </button>
        )}
      </div>
      <p className="text-[10px] text-gray-500 mt-2">
        Instagram doesn't allow direct web share — tap it to copy your link, then paste it into your Story or bio.
      </p>
    </div>
  );
};

